import { fixed, shopSpeak } from '../../math/format';
import { stockToRemove } from '../../math/tolerance';
import { mcOptions } from '../helpers';
import type { QuestionTemplate } from '../types';

export const TENTHS_TEMPLATES: readonly QuestionTemplate[] = [
  {
    id: 'tenths.name',
    topicId: 'tenths',
    skill: 'Naming a small number the way the shop says it',
    gen: (rng) => {
      const tenths = rng.pick([1, 2, 3, 5, 8]);
      const value = tenths / 10000;

      return {
        kind: 'mc',
        prompt: `How much is ${fixed(value, 4)}"?`,
        why: `0.0010" is one thou. ${fixed(value, 4)}" is ${tenths} ten-thousandths, said out loud as "${shopSpeak(value)}".`,
        steps: [
          { kind: 'note', text: 'A thou is 0.001". A tenth is 0.0001" — a tenth OF a thou.' },
          { kind: 'compute', text: `${fixed(value, 4)}" has ${tenths} in the fourth decimal place.` },
          { kind: 'result', text: `So it is ${shopSpeak(value)}.` },
        ],
        ...mcOptions(rng, `${tenths} ${tenths === 1 ? 'tenth' : 'tenths'}`, [
          {
            label: `${tenths} thou`,
            trap: `${tenths} thou would be ${fixed(tenths / 1000, 4)}" — ten times bigger. Count the decimal places.`,
          },
          {
            label: `${tenths} hundredths`,
            trap: `${tenths} hundredths is ${fixed(tenths / 100, 4)}". Nowhere near.`,
          },
          {
            label: `${tenths} thousandths`,
            trap: 'Thousandths are thou, the third decimal place. This number goes one further.',
          },
        ]),
      };
    },
  },
  {
    id: 'tenths.remove',
    topicId: 'tenths',
    skill: 'Working out how much to take off',
    gen: (rng) => {
      const target = rng.step(0.5, 1.5, 0.125);
      const over = rng.pick([0.0005, 0.001, 0.0015, 0.002, 0.0025]);
      const measured = Math.round((target + over) * 10000) / 10000;
      const solved = stockToRemove(measured, target);
      const correct = `${fixed(solved.value.onDiameter, 4)}"`;

      return {
        kind: 'mc',
        prompt: `You mic ${fixed(measured, 4)}" and need ${fixed(target, 4)}". How much comes off?`,
        why: `${fixed(measured, 4)} − ${fixed(target, 4)} = ${fixed(solved.value.onDiameter, 4)}", which is ${shopSpeak(solved.value.onDiameter)}. On a diameter you take half that per side.`,
        steps: solved.steps,
        ...mcOptions(rng, correct, [
          {
            label: `${fixed(solved.value.perSide, 4)}"`,
            trap: 'That is the per-side amount. The question asks how much comes off the diameter, which is double.',
          },
          {
            label: `${fixed(solved.value.onDiameter * 10, 4)}"`,
            trap: 'Decimal point is one place out. Line the two numbers up before you subtract.',
          },
          {
            label: `${fixed(solved.value.onDiameter / 10, 4)}"`,
            trap: 'Decimal point is one place out the other way.',
          },
        ]),
      };
    },
  },
  {
    id: 'tenths.tightest',
    topicId: 'tenths',
    skill: 'Ranking small numbers',
    gen: (rng) => {
      const digit = rng.int(1, 5);
      const correct = fixed(digit / 10000, 4);

      return {
        kind: 'mc',
        prompt: 'Which of these is the tightest?',
        why: `More zeros after the point means a smaller number. ${correct}" is ${shopSpeak(digit / 10000)}.`,
        steps: [
          { kind: 'note', text: 'Line them up by decimal place, not by how many digits they have.' },
          { kind: 'result', text: `${correct}" has the most zeros after the point, so it is the smallest.` },
        ],
        ...mcOptions(rng, `${correct}"`, [
          {
            label: `${fixed(digit / 1000, 3)}"`,
            trap: 'Ten times bigger. Count the places after the decimal point.',
          },
          {
            label: `${fixed(digit / 100, 2)}"`,
            trap: 'A hundred times bigger than the tightest one here.',
          },
          {
            label: `${fixed(digit / 10, 1)}"`,
            trap: 'That is the loosest of the four, not the tightest.',
          },
        ]),
      };
    },
  },
  {
    id: 'tenths.numeric',
    topicId: 'tenths',
    skill: 'Subtracting to four places',
    gen: (rng) => {
      const target = rng.step(0.25, 2, 0.0625);
      const over = rng.pick([0.0008, 0.0012, 0.0016, 0.0024]);
      const measured = Math.round((target + over) * 10000) / 10000;
      const solved = stockToRemove(measured, target);

      return {
        kind: 'numeric',
        prompt: `Part is at ${fixed(measured, 4)}", print wants ${fixed(target, 4)}". How much per side?`,
        sub: 'Answer in inches, four places.',
        answer: solved.value.perSide,
        tolerance: 0.00005,
        unit: '"',
        places: 4,
        why: `${fixed(solved.value.onDiameter, 4)}" comes off the diameter, so ${fixed(solved.value.perSide, 4)}" per side.`,
        steps: solved.steps,
      };
    },
  },
];
